import { X } from 'lucide-react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '~/components/ui/badge'
import { Input } from '~/components/ui/input'
import { addTag, TAG_MAX_LENGTH, TAGS_PER_PATH } from '~/pages/backoffice/admin/sessions/tags-input'

type IProps = {
  value: string[]
  onChange: (tags: string[]) => void
  /** Étiquettes déjà posées sur les parcours de l'organisation, proposées à la saisie. */
  suggestions: readonly string[]
}

/** Les étiquettes libres d'un parcours (#29) : on les tape, Entrée les pose, la croix les retire. */
function TagsField({ value, onChange, suggestions }: IProps) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState('')
  const listId = useId()

  const full = value.length >= TAGS_PER_PATH

  // La saisie ne s'efface que si elle a été posée : refusée (doublon mis à
  // part, déjà présent), elle reste sous les yeux plutôt que de disparaître.
  const commit = () => {
    const next = addTag(value, draft)
    const isDuplicate = draft.trim() !== '' && next.length === value.length && !full
    if (next.length > value.length || isDuplicate) setDraft('')
    onChange(next)
  }

  return (
    <div className="flex flex-col gap-1.5 sm:col-span-2">
      <label className="text-sm font-medium" htmlFor={`${listId}-input`}>{t('sessions.tags.label')}</label>
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {value.map(tag => (
            <li key={tag}>
              <Badge variant="secondary" className="gap-1 pr-1">
                {tag}
                <button
                  type="button"
                  className="rounded-sm opacity-70 hover:opacity-100"
                  aria-label={t('sessions.tags.remove', { tag })}
                  onClick={() => onChange(value.filter(other => other !== tag))}
                >
                  <X className="size-3" />
                </button>
              </Badge>
            </li>
          ))}
        </ul>
      )}
      <Input
        id={`${listId}-input`}
        list={listId}
        value={draft}
        maxLength={TAG_MAX_LENGTH}
        disabled={full}
        placeholder={t(full ? 'sessions.tags.full' : 'sessions.tags.placeholder')}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={(e) => {
          // Pendant une composition (IME), Entrée valide le caractère, pas l'étiquette.
          if ((e.key === 'Enter' || e.key === ',') && !e.nativeEvent.isComposing) {
            e.preventDefault()
            commit()
          }
        }}
        onBlur={commit}
      />
      <datalist id={listId}>
        {suggestions.filter(tag => !value.includes(tag)).map(tag => <option key={tag} value={tag} />)}
      </datalist>
      <p className="text-xs text-muted-foreground">{t('sessions.tags.hint')}</p>
    </div>
  )
}

export default TagsField
